import { useMemo, useRef, useState } from "react";
import { X, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

function Chips({ value, readOnly, onChange, testid }) {
  return value.map((t) => (
    <span
      key={t}
      className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2.5 py-0.5 font-mono-tight text-[11px] text-white"
      data-testid={`${testid}-chip-${t}`}
    >
      {t}
      {!readOnly && (
        <button
          type="button"
          onClick={() => onChange(value.filter((x) => x !== t))}
          className="rounded-full text-slate-400 transition-colors hover:text-white"
          aria-label={`Remove ${t}`}
          data-testid={`${testid}-remove-${t}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  ));
}

function OptionsMenu({ suggestions, options, text, testid, onPick, onCustom }) {
  return (
    <div
      className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-md border border-slate-200 bg-white p-1 shadow-lg"
      data-testid={`${testid}-options`}
    >
      {suggestions.map((o) => (
        <button
          key={o}
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => { e.preventDefault(); onPick(o); }}
          className="block w-full rounded-sm px-2.5 py-1.5 text-left font-mono-tight text-xs text-slate-700 transition-colors hover:bg-slate-900 hover:text-white"
          data-testid={`${testid}-option-${o}`}
        >
          {o}
        </button>
      ))}
      {text.trim() && !options.includes(text.trim()) && (
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => { e.preventDefault(); onCustom(); }}
          className="block w-full rounded-sm border-t border-slate-100 px-2.5 py-1.5 text-left font-mono-tight text-xs text-slate-500 hover:bg-slate-100"
          data-testid={`${testid}-option-custom`}
        >
          + add custom "{text.trim()}"
        </button>
      )}
      {!suggestions.length && !text.trim() && (
        <p className="px-2.5 py-1.5 font-mono-tight text-xs text-slate-400">All known values added — type a custom one.</p>
      )}
    </div>
  );
}

// Text entry state for the chip list: comma/Enter commit, backspace pops, Escape closes the menu.
function useListEntry({ value, onChange, readOnly, options }) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);

  const commit = (raw) => {
    const items = raw.split(",").map((s) => s.trim()).filter(Boolean);
    if (!items.length) return;
    onChange([...value, ...items.filter((i) => !value.includes(i))]);
    setText("");
  };
  const onKeyDown = (e) => {
    if (readOnly) return;
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(text); return; }
    if (e.key === "Backspace" && !text && value.length) { onChange(value.slice(0, -1)); return; }
    if (e.key === "Escape") setOpen(false);
  };
  const onInput = (e) => {
    const v = e.target.value;
    if (v.includes(",")) commit(v);
    else setText(v);
    if (options) setOpen(true);
  };
  const pick = (o) => { onChange([...value, o]); setText(""); };
  return { text, open, setOpen, commit, onKeyDown, onInput, pick };
}

export function ListInput({ value = [], onChange, readOnly, placeholder, testid, className, options }) {
  const wrapRef = useRef(null);
  const entry = useListEntry({ value, onChange, readOnly, options });
  const { text, open, setOpen } = entry;

  const suggestions = useMemo(() => {
    if (!options) return [];
    const q = text.trim().toLowerCase();
    return options.filter((o) => !value.includes(o) && (!q || o.toLowerCase().includes(q))).slice(0, 60);
  }, [options, value, text]);

  const onBlur = (e) => {
    if (wrapRef.current?.contains(e.relatedTarget)) return;
    entry.commit(text);
    setOpen(false);
  };
  const editable = !readOnly;

  return (
    <div ref={wrapRef} className={cn("relative", className)} onBlur={onBlur}>
      <div
        className={cn(
          "flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-slate-900/10",
          readOnly && "bg-slate-50",
        )}
        data-testid={testid}
      >
        <Chips value={value} readOnly={readOnly} onChange={onChange} testid={testid} />
        {editable && (
          <input
            value={text}
            onChange={entry.onInput}
            onKeyDown={entry.onKeyDown}
            onFocus={() => options && setOpen(true)}
            placeholder={value.length ? "" : placeholder}
            className="min-w-[120px] flex-1 bg-transparent font-mono-tight text-sm outline-none placeholder:text-slate-400"
            data-testid={`${testid}-input`}
            autoComplete="off"
          />
        )}
        {editable && options && (
          <button
            type="button"
            tabIndex={-1}
            onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o); }}
            className="ml-auto grid h-6 w-6 place-items-center rounded text-slate-400 hover:text-slate-900"
            aria-label="Show options"
            data-testid={`${testid}-toggle`}
          >
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
          </button>
        )}
        {readOnly && !value.length && <span className="font-mono-tight text-xs text-slate-400">—</span>}
      </div>
      {editable && options && open && (
        <OptionsMenu suggestions={suggestions} options={options} text={text} testid={testid} onPick={entry.pick} onCustom={() => entry.commit(text)} />
      )}
    </div>
  );
}
